/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { applyMiddleware, compose, createStore } from "redux";
import thunkMiddleware from "redux-thunk";
import { createLogger } from "redux-logger";
import rootReducer from "./index";
import {
  DRAG_OVER_STOPPED,
  DRAG_OVER_TARGET,
  PARTICIPANT_MIC_SAMPLE,
  SEEK_TO,
  SET_INITIAL_ASSETS_LOADED,
  SET_PLAYER_PLAYBACK_PROGRESS,
  SET_PLAYER_STATE,
  SET_SELECTION,
  UPDATE_RECORDING_DURATION,
} from "constants/ActionTypes";
import { Action, AppAction } from "types/AppAction";
import { INITIAL_STATE } from "reducers/InitialState";
import { undoEnhancer } from "reducers/Undo";
import { behanceApi } from "api/BehanceApi";
import {
  Middleware,
  Store,
  StoreEnhancer,
  ThunkDispatch,
} from "@reduxjs/toolkit";
import { AppState } from "types/AppState";
import { waveformApi } from "api/WaveformApi";

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose;
  }
}

const isProduction = JSON.parse(process.env.IS_PRODUCTION ?? "false");
const forceProdMode = localStorage.getItem("redux-debug-mode") == "false";
const forceDebugMode = localStorage.getItem("redux-debug-mode") == "true";
const shouldUseDebugMode = forceDebugMode
  ? true
  : forceProdMode
    ? false
    : !isProduction;

const composeEnhancers = shouldUseDebugMode
  ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose
  : compose;

function sortObject(object: any): any {
  if (!object || typeof object !== "object") {
    return object;
  }

  const keys = Object.keys(object).sort();
  return keys.reduce((result: { [index: string]: any }, key) => {
    result[key] = sortObject(object[key]);
    return result;
  }, {});
}
const loggerMiddleware = createLogger({
  predicate: (getState: Function, action: Action) => {
    let skippableActions = [
      SET_PLAYER_PLAYBACK_PROGRESS,
      SEEK_TO,
      SET_PLAYER_STATE,
      DRAG_OVER_STOPPED,
      DRAG_OVER_TARGET,
      PARTICIPANT_MIC_SAMPLE,
      UPDATE_RECORDING_DURATION,
      SET_INITIAL_ASSETS_LOADED,
    ];

    if (process.env.NODE_ENV !== "development") {
      skippableActions.push(
        SET_SELECTION,
        "profiles/executeQuery/rejected",
        "profiles/subscriptions/unsubscribeQueryResult"
      );
    }

    return !skippableActions.includes(action.type);
  },
  actionTransformer: (action: Action) => {
    const { type, ...rest } = action;
    const orderedRest = sortObject(rest);
    return { type, ...orderedRest };
  },
});

const nullMiddleware: Middleware =
  (api) =>
  (next): typeof next =>
    next;

const middleware: StoreEnhancer<{
  dispatch: ThunkDispatch<AppState, any, Action>;
}> = applyMiddleware<
  ThunkDispatch<AppState, any, Action>,
  {},
  any,
  any,
  AppState
>(
  thunkMiddleware,
  shouldUseDebugMode ? loggerMiddleware : nullMiddleware,
  behanceApi.middleware as any,
  waveformApi.middleware as any
);

export const setupStore = (initialState = INITIAL_STATE): StoreType =>
  createStore<
    AppState,
    Action,
    {
      dispatch: ThunkDispatch<AppState, any, Action>;
    },
    {}
  >(rootReducer, initialState, composeEnhancers(middleware, undoEnhancer()));

const store: Store<AppState, Action> & {
  dispatch: ThunkDispatch<AppState, any, Action>;
} = setupStore();

export type StoreType = typeof store;

declare module "react-redux" {
  export function useStore(): StoreType;
}

declare module "react-redux" {
  export function useStore(): Store<AppState, Action>;
}

export default store;
